import { useState, useEffect } from 'react';
import { X, Save, StickyNote } from 'lucide-react';
import { useMutation, useQueryClient } from 'react-query';
import { api } from '../lib/api';
import toast from 'react-hot-toast';

interface Student {
  Name: string;
  Vorname: string;
  Klasse: string;
  Unid?: string;
  Memo?: string;
}

interface MemoModalProps {
  student: Student;
  isOpen: boolean;
  onClose: () => void;
}

const MemoModal: React.FC<MemoModalProps> = ({ student, isOpen, onClose }) => {
  const [memo, setMemo] = useState(student.Memo || '');
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  // Reset memo when student changes
  useEffect(() => {
    setMemo(student.Memo || '');
  }, [student.Memo, student.Unid]);

  const updateMemoMutation = useMutation(
    async (memoData: { memo: string }) => {
      if (!student.Unid) {
        throw new Error('Student Unid is required');
      }
      
      const response = await api.put(`/students/memo/${student.Unid}`, memoData);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Memo erfolgreich gespeichert');
        queryClient.invalidateQueries(['attendance']);
        queryClient.invalidateQueries(['lernbuero-attendance']);
        onClose();
      },
      onError: (error: any) => {
        console.error('Error updating memo:', error);
        toast.error('Fehler beim Speichern des Memos');
      },
    }
  );

  const handleSave = async () => {
    if (!student.Unid) {
      toast.error('Student-ID fehlt');
      return;
    }

    setIsLoading(true);
    try {
      await updateMemoMutation.mutateAsync({ memo: memo.trim() });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setMemo(student.Memo || '');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <StickyNote className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Memo bearbeiten
              </h2>
              <p className="text-sm text-gray-600">
                {student.Vorname} {student.Name} ({student.Klasse})
              </p>
            </div>
          </div>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 p-2 rounded hover:bg-gray-100 transition-colors"
            title="Schließen"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <label htmlFor="memo-textarea" className="block text-sm font-medium text-gray-700 mb-2">
              Memo-Text
            </label>
            <textarea
              id="memo-textarea"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder="Hier können Sie Notizen zu diesem Schüler eingeben..."
              className="w-full h-40 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              disabled={isLoading || updateMemoMutation.isLoading}
            />
          </div>
          
          <div className="text-xs text-gray-500 mb-4">
            Zeichen: {memo.length}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleCancel}
            disabled={isLoading || updateMemoMutation.isLoading}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading || updateMemoMutation.isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
          >
            {(isLoading || updateMemoMutation.isLoading) ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Speichern...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Speichern
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MemoModal;
